import { useState } from "react";
import { Button, Checkbox } from "@a2z/ui";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api/client";
import { endpoints } from "@/lib/api/endpoints";
import type {
  CashierLaneBill,
  Invoice,
  MenuItem,
  OffPremiseGridEntry,
  RestaurantSettings,
  TableGridArea,
} from "@/lib/api/types";
import { useI18n, useTranslate } from "@/lib/i18n";
import { PERMISSIONS, hasPermission, useSession } from "@/modules/auth/useSession";
import { groupPlates } from "@/modules/pos/lib/groupPlates";
import { canAnswerQrRequests, canSendToKitchen } from "@/modules/pos/lib/kitchenRouting";
import { formatCheckClock } from "@/modules/pos/lib/preBillCheck";
import { DoodleBadge } from "@/shared/doodles/DoodleBadge";
import { useErrorMessage } from "@/shared/errors";
import { invalidateInvoiceMutation } from "@/shared/invalidation";
import { queryKeys, useApiQuery } from "@/shared/queries";
import { usePrivateToast } from "@/shared/usePrivateToast";
import { useInvoiceCommands } from "@/shared/useInvoiceCommands";
import { useLocalName } from "@/shared/localName";
import { LineChoice, pickChoiceLabels } from "@/modules/kitchen/components/PaperKitchen";
import { OutOfStockFireConfirm } from "@/modules/pos/components/OutOfStockFireConfirm";
import { outOfStockDrafts, type OutOfStockDraft } from "@/modules/pos/lib/outOfStockDrafts";

/** What a waiting ticket needs of its bill: its id and its plates (P7.6). */
export type CashierLaneTicketBill = Pick<Invoice, "id" | "items">;

/** The lane reads' latest landed plates per bill, and when they landed (P7.6). */
type LandedLanePlates = {
  at: number;
  bills: ReadonlyMap<string, CashierLaneBill>;
};
const NOTHING_LANDED: LandedLanePlates = { at: 0, bills: new Map() };

/** Most bills one lane read names — the server's `CASHIER_LANE_MAX_BILLS`. */
const CASHIER_LANE_MAX_BILLS = 100;

/** One bill waiting on the cashier, and why. */
export type CashierWaitingEntry = {
  invoiceId: string;
  /** The table's name, or null for a takeaway or delivery bill. */
  tableName: string | null;
  dailySequence: number;
  /** A waiter's "Send to cashier" (D120), and when. */
  fromWaiterAt: string | null;
  /** A diner's QR requests not yet accepted or rejected (D8). */
  fromCustomerCount: number;
};

/**
 * EVERY BILL WAITING ON THE CASHIER (founder, 2026-10-07): sent to the cashier
 * by a waiter, or carrying a diner's QR request. Oldest waiter request first,
 * then the QR ones.
 */
export function cashierWaitingEntries(
  areas: readonly TableGridArea[] | undefined,
  offPremise: readonly OffPremiseGridEntry[] | undefined,
): CashierWaitingEntry[] {
  const out: CashierWaitingEntry[] = [];
  const add = (
    bill: { invoiceId: string; dailySequence: number; cashierRequestedAt?: string | null; pendingItemCount?: number },
    tableName: string | null,
  ) => {
    const fromWaiterAt = bill.cashierRequestedAt ?? null;
    const fromCustomerCount = bill.pendingItemCount ?? 0;
    if (!fromWaiterAt && fromCustomerCount === 0) return;
    out.push({ invoiceId: bill.invoiceId, tableName, dailySequence: bill.dailySequence, fromWaiterAt, fromCustomerCount });
  };
  for (const area of areas ?? []) {
    for (const table of area.tables) {
      for (const bill of table.openInvoices) add(bill, table.name);
    }
  }
  for (const bill of offPremise ?? []) add(bill, null);
  return out.sort((a, b) => {
    if (a.fromWaiterAt && b.fromWaiterAt) return a.fromWaiterAt.localeCompare(b.fromWaiterAt);
    if (a.fromWaiterAt) return -1;
    if (b.fromWaiterAt) return 1;
    return a.dailySequence - b.dailySequence;
  });
}

/**
 * Who MAY see the lane: a till session holding a grant that could answer what
 * waits in it — send a waiter's order (`ITEM_FIRE`) or take a diner's request
 * (`INVOICE_APPEND`). Whether it CAN answer also depends on the store's D121
 * switch (`ordersViaCashier` adds `INVOICE_CLOSE` to both), which the lane
 * reads before it draws anything (`CashierWaitingLane`).
 */
export function canAnswerCashierLane(session: Parameters<typeof hasPermission>[0]): boolean {
  return (
    hasPermission(session, PERMISSIONS.POS_USE) &&
    (hasPermission(session, PERMISSIONS.ITEM_FIRE) || hasPermission(session, PERMISSIONS.INVOICE_APPEND))
  );
}

/**
 * THE «بانتظار أمين الصندوق» LANE — on the TILL FLOOR since T8 / D173 (it lived on
 * the floor's Kitchen tab, founder 2026-10-07). Everyone who fires sees what
 * is waiting on them — a waiter's order to send, a diner's QR order to accept
 * — above the areas of the Tables view, in every kitchen workflow and on both
 * tills (`TableGridPage`), and on the back office's live floor
 * (`FloorLiveView`). The cashier is not given the kitchen board for it: T8
 * refused `KITCHEN_VIEW` on CASHIER.
 *
 * Each bill is a ticket in the board's own style: a waiter's order with "Send
 * to kitchen", a diner's QR order with "Reject" and "Accept". It draws nothing
 * while nothing waits, and nothing — and reads no bill — for a session that can
 * neither send nor answer: in a D121 store (orders via the cashier) a waiter
 * holds `ITEM_FIRE` and `INVOICE_APPEND` but not `INVOICE_CLOSE`, and every
 * waiter's floor used to carry a section of their own sent bills as tickets
 * with no buttons, each fetched on every waiter device. Until the settings
 * answer, the switch counts as ON (only the cashier acts). The floor reads and
 * the settings are the till's own (shared cache); the waiting bills' dishes
 * are one read for the whole lane (P7.6).
 */
export function CashierWaitingLane() {
  const t = useTranslate();
  const { locale } = useI18n();
  const { data: session } = useSession();
  const canAnswer = canAnswerCashierLane(session);
  const settingsQuery = useApiQuery<RestaurantSettings>(queryKeys.settings, endpoints.restaurant.settings, {
    enabled: canAnswer,
    retry: false,
  });
  const settings = settingsQuery.data ?? null;
  // D121 — a missing field (an older API) reads as off; no answer yet reads as on.
  const viaCashier = settings ? (settings.ordersViaCashier ?? false) : true;
  const mayFire = canSendToKitchen(session, viaCashier);
  const mayAnswer = canAnswerQrRequests(session, viaCashier);
  const mayAct = canAnswer && (mayFire || mayAnswer);
  const grid = useApiQuery<TableGridArea[]>(queryKeys.grid, endpoints.restaurant.grid, {
    enabled: mayAct,
  });
  const offPremise = useApiQuery<OffPremiseGridEntry[]>(
    [...queryKeys.offPremiseOpen, "grid"],
    endpoints.restaurant.gridOffPremise,
    { enabled: mayAct, retry: false },
  );
  const entries = cashierWaitingEntries(grid.data, offPremise.data);
  // P7.6: the waiting bills' plates in one read (in pieces of
  // CASHIER_LANE_MAX_BILLS), not one whole bill per ticket.
  const plates = useCashierLanePlates(
    entries.map((entry) => entry.invoiceId),
    mayAct,
  );

  if (!mayAct || entries.length === 0) return null;
  return (
    <section
      data-ui="kitchen-lane"
      data-lane="CASHIER"
      data-place="floor"
      data-testid="cashier-waiting-lane"
      aria-label={t((m) => m.kitchen.laneCashier)}
      className="flex min-w-0 flex-col gap-2.5"
    >
      <h2 data-ui="kitchen-lane-head" className="flex shrink-0 items-center gap-2">
        <span aria-hidden="true" data-ui="kitchen-lane-dot" />
        <span className="whitespace-nowrap">{t((m) => m.kitchen.laneCashier)}</span>
        <span data-ui="kitchen-lane-count" data-testid="kitchen-lane-count-CASHIER" className="tabular-nums">
          {entries.length}
        </span>
      </h2>
      <div data-ui="kitchen-lane-body" className="grid gap-2.5 [grid-template-columns:repeat(auto-fill,minmax(min(100%,17rem),1fr))]">
        {entries.map((entry) => (
          <WaitingTicket
            key={entry.invoiceId}
            entry={entry}
            bill={plates.get(entry.invoiceId) ?? null}
            placeLabel={entry.tableName ?? t((m) => m.pos.invoiceNumber, { sequence: entry.dailySequence })}
            clock={
              entry.fromWaiterAt
                ? formatCheckClock(entry.fromWaiterAt, settings?.timezone, locale === "en" ? "en" : "ar")
                : null
            }
            mayFire={mayFire}
            mayAnswer={mayAnswer}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * The lane's bills' DRAFT and PENDING plates (P7.6), keyed by bill: one
 * `GET /invoices/cashier-lane` per piece of `CASHIER_LANE_MAX_BILLS` sorted
 * ids — the lane until P7.6 read every waiting bill whole. A bill the read
 * does not answer for is absent, as its own failed read left it.
 *
 * A bill joining or leaving the lane changes its piece's key, and a new key
 * starts with no data. While that piece's read is out, each of its bills keeps
 * the plates its latest landed lane read gave it (held in this hook's state,
 * not in the cache, which may already have let the old key go) — so a ticket
 * already drawn keeps its lines and its buttons, as its own per-bill read kept
 * them before P7.6 — and a bill no landed read has named yet waits for its
 * read, its buttons disabled.
 */
function useCashierLanePlates(
  invoiceIds: readonly string[],
  enabled: boolean,
): ReadonlyMap<string, CashierLaneTicketBill> {
  const sorted = [...new Set(invoiceIds)].sort();
  const pieces: string[][] = [];
  for (let at = 0; at < sorted.length; at += CASHIER_LANE_MAX_BILLS) {
    pieces.push(sorted.slice(at, at + CASHIER_LANE_MAX_BILLS));
  }
  const reads = useQueries({
    queries: pieces.map((ids) => ({
      queryKey: queryKeys.cashierLane(ids),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        apiFetch<CashierLaneBill[]>(endpoints.restaurant.cashierLane(ids), { signal }),
      enabled,
    })),
  });
  // The plates each lane bill's latest landed read gave it, kept across a
  // key change; adjusted while rendering when a newer read lands, as
  // `usePaperNavCounts` keeps its entry. Only the lane's current bills stay.
  const [landed, setLanded] = useState<LandedLanePlates>(NOTHING_LANDED);
  const newest = Math.max(0, ...reads.map((read) => (read.data ? read.dataUpdatedAt : 0)));
  let kept = landed;
  if (newest > landed.at) {
    const inLane = new Set(sorted);
    const bills = new Map(
      [...landed.bills].filter(([invoiceId]) => inLane.has(invoiceId)),
    );
    for (const read of reads) {
      for (const bill of read.data ?? []) bills.set(bill.invoiceId, bill);
    }
    kept = { at: newest, bills };
    setLanded(kept);
  }
  const byBill = new Map<string, CashierLaneTicketBill>();
  reads.forEach((read, index) => {
    // A piece whose read is out leaves its bills on their last landed plates.
    const answered = read.data
      ? read.data
      : pieces[index].flatMap((invoiceId) => {
          const last = kept.bills.get(invoiceId);
          return last ? [last] : [];
        });
    for (const bill of answered) {
      byBill.set(bill.invoiceId, { id: bill.invoiceId, items: bill.items });
    }
  });
  return byBill;
}

function WaitingTicket({
  entry,
  bill,
  placeLabel,
  clock,
  mayFire,
  mayAnswer,
}: {
  entry: CashierWaitingEntry;
  bill: CashierLaneTicketBill | null;
  placeLabel: string;
  clock: string | null;
  mayFire: boolean;
  mayAnswer: boolean;
}) {
  const t = useTranslate();
  const localName = useLocalName();
  const queryClient = useQueryClient();
  const { dispatchCommand } = useInvoiceCommands();
  const toast = usePrivateToast();
  const errorMessage = useErrorMessage();
  const [busy, setBusy] = useState(false);
  /*
   * Ticked plates (founder, 2026-10-07): the buttons act on these when any are
   * ticked, and on the whole order when none are — the bill's own rule.
   */
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  /** The `N×` line asking "1 or all?", the board's own question. */
  const [choosing, setChoosing] = useState<string | null>(null);
  /** T11 / D173 — 86'd dishes in what «إرسال إلى المطبخ» is about to send. */
  const [outAtFire, setOutAtFire] = useState<OutOfStockDraft[] | null>(null);
  // The till's own menu read (shared cache); only a session that may send reads it.
  const menu = useApiQuery<MenuItem[]>(queryKeys.menuItems, endpoints.restaurant.menuItems, {
    enabled: mayFire,
    retry: false,
  });

  const drafts = bill?.items.filter((item) => item.fulfillmentState === "DRAFT") ?? [];
  const pending = bill?.items.filter((item) => item.fulfillmentState === "PENDING") ?? [];
  const showDrafts = entry.fromWaiterAt !== null && drafts.length > 0;
  const lines = groupPlates([...(showDrafts ? drafts : []), ...pending]);

  /** On the ticked plates, or on all of them when none are ticked. */
  const run = async (type: "fireItems" | "acceptPendingItems" | "rejectPendingItems") => {
    if (!bill || busy) return;
    setBusy(true);
    try {
      const pool = type === "fireItems" ? drafts : pending;
      const chosen = pool.filter((item) => picked.has(item.id));
      const itemIds = (chosen.length > 0 ? chosen : pool).map((item) => item.id);
      await dispatchCommand({ type, invoiceId: bill.id, payload: { itemIds } });
      setPicked(new Set());
      setChoosing(null);
      await invalidateInvoiceMutation(queryClient, bill.id);
    } catch (failure) {
      toast.error(errorMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  /** Send to the kitchen — asking first when an 86'd dish is in it (warn, never block). */
  const requestFire = () => {
    const chosen = drafts.filter((item) => picked.has(item.id));
    const out = outOfStockDrafts(chosen.length > 0 ? chosen : drafts, menu.data);
    if (out.length > 0) {
      setOutAtFire(out);
      return;
    }
    void run("fireItems");
  };

  return (
    <article
      data-ui="kitchen-ticket"
      data-lane="CASHIER"
      data-testid={`cashier-waiting-${entry.invoiceId}`}
      className="relative flex shrink-0 flex-col overflow-hidden"
    >
      <header data-ui="kitchen-ticket-head" className="flex items-center gap-1.5">
        <DoodleBadge id="servicebell" size={28} animate={false} />
        <span data-ui="kitchen-ticket-number" className="whitespace-nowrap tabular-nums">
          #{entry.dailySequence}
        </span>
        <span data-ui="kitchen-ticket-place" className="truncate">
          <bdi>{placeLabel}</bdi>
        </span>
        <span className="flex-1" />
        <span data-ui="kitchen-ticket-time" className="whitespace-nowrap">
          {entry.fromWaiterAt
            ? t((m) => m.kitchen.fromWaiter, { time: clock ?? "" })
            : t((m) => m.kitchen.fromCustomer, { number: entry.fromCustomerCount })}
        </span>
      </header>
      <div data-ui="kitchen-ticket-lines" className="flex flex-col">
        {lines.map((line) => {
          const pickedCount = line.plates.filter((plate) => picked.has(plate.id)).length;
          const name = localName({ nameAr: line.plateNameAr, nameEn: line.plateNameEn });
          // A tap TICKS the line, like the check beside it — nothing leaves
          // the card until a footer button says so (founder, 2026-10-07).
          // An `N×` line asks "1 or all N?" first, the board's way.
          const togglePick = () => {
            if (pickedCount > 0) {
              setPicked((current) => {
                const next = new Set(current);
                for (const plate of line.plates) next.delete(plate.id);
                return next;
              });
              return;
            }
            if (line.quantity > 1) {
              setChoosing((current) => (current === line.key ? null : line.key));
              return;
            }
            setPicked((current) => new Set(current).add(line.plates[0].id));
          };
          return (
            <div key={line.key} className="flex flex-col">
              <div data-ui="kitchen-line-row" className="flex items-center">
                <span data-ui="kitchen-line-pick" className="flex shrink-0 items-center">
                  <Checkbox
                    size="md"
                    checked={pickedCount > 0}
                    disabled={busy}
                    aria-label={t((m) => m.pos.pickItem)}
                    data-testid={`cashier-waiting-pick-${line.key}`}
                    onChange={() => {
                      // The check itself is a plain checkbox: the whole line.
                      setChoosing(null);
                      setPicked((current) => {
                        const next = new Set(current);
                        for (const plate of line.plates) {
                          if (pickedCount > 0) next.delete(plate.id);
                          else next.add(plate.id);
                        }
                        return next;
                      });
                    }}
                  />
                </span>
                <button
                  type="button"
                  data-ui="kitchen-line"
                  data-testid={`cashier-waiting-line-${line.key}`}
                  aria-pressed={pickedCount > 0}
                  aria-expanded={line.quantity > 1 ? choosing === line.key : undefined}
                  disabled={busy}
                  onClick={togglePick}
                  className="flex w-full items-center gap-2 text-start"
                >
                  <span data-ui="kitchen-line-qty" className="shrink-0 tabular-nums">
                    {pickedCount > 0 && pickedCount < line.quantity
                      ? `${pickedCount}/${line.quantity}×`
                      : `${line.quantity}×`}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span data-ui="kitchen-line-name">
                      <bdi>{name}</bdi>
                    </span>
                    {line.note ? <span data-ui="kitchen-line-note">{"★ "}<bdi>{line.note}</bdi></span> : null}
                  </span>
                </button>
              </div>
              {choosing === line.key && line.quantity > 1 ? (
                <LineChoice
                  kind="pick"
                  {...pickChoiceLabels(t, line.quantity)}
                  name={name}
                  busy={busy}
                  testId={`cashier-waiting-choice-${line.key}`}
                  onPick={(all) => {
                    setChoosing(null);
                    setPicked((current) => {
                      const next = new Set(current);
                      for (const plate of all ? line.plates : line.plates.slice(0, 1)) next.add(plate.id);
                      return next;
                    });
                  }}
                  onCancel={() => setChoosing(null)}
                />
              ) : null}
            </div>
          );
        })}
      </div>
      <footer data-ui="kitchen-ticket-foot" className="flex flex-wrap items-center justify-end gap-2">
        {showDrafts && mayFire ? (
          <Button
            variant="primary"
            size="sm"
            disabled={busy || !bill}
            onClick={requestFire}
            data-testid={`cashier-waiting-fire-${entry.invoiceId}`}
          >
            {t((m) => m.pos.sendToKitchen)}
          </Button>
        ) : null}
        {pending.length > 0 && mayAnswer ? (
          <>
            <Button
              variant="outline"
              size="sm"
              disabled={busy || !bill}
              onClick={() => void run("rejectPendingItems")}
              data-testid={`cashier-waiting-reject-${entry.invoiceId}`}
            >
              {t((m) => m.pos.rejectPending)}
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={busy || !bill}
              onClick={() => void run("acceptPendingItems")}
              data-testid={`cashier-waiting-accept-${entry.invoiceId}`}
            >
              {t((m) => m.pos.acceptPending)}
            </Button>
          </>
        ) : null}
      </footer>
      <OutOfStockFireConfirm
        items={outAtFire}
        onCancel={() => setOutAtFire(null)}
        onSend={() => {
          setOutAtFire(null);
          void run("fireItems");
        }}
      />
    </article>
  );
}

